import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CodeEditor, { type MonacoEditor, type MonacoNs } from "../components/CodeEditor";
import Toolbar from "../components/Toolbar";
import SensorPanel from "../components/SensorPanel";
import OpenDialog, { type OpenResult } from "../components/OpenDialog";
import ApiReference from "../components/ApiReference";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { useAuth } from "../auth/AuthContext";
import { useSimulator } from "../worker/useSimulator";
import type { SimEvent } from "../worker/protocol";
import { api, ApiError } from "../api/client";
import { downloadScript, ensurePyName } from "../scripts/files";
import mekaboyLogoWhite from "../assets/mekaboy-logo-white.png";
import mctLogoSmall from "../assets/mct-logo-small.png";

const STARTER = `# Mekaboy MicroPython Simulator
# Write robot code, then press Run (Ctrl+Enter).

import Hiwonder
import time

tony = Hiwonder.Tonybot()
print("Hello, Mekaboy!")

tony.runActionGroup(21, 2)   # CYCLE_FORWARD, 2 cycles
tony.waitForStop()
tony.moveHeadAngle(45)
print("Done.")
`;

type Speed = "realtime" | "instant";
type BottomTab = "console" | "actionlog";

const DEV_ICON: Record<SimEvent["dev"], string> = {
  servo: "🦿",
  rgb: "🌈",
  buzzer: "🔊",
  action: "🤖",
  ultrasonic: "📏",
  imu: "⚖️",
  battery: "🔋",
  pose: "📍",
  system: "⚙️",
};

function fmtTime(ms: number): string {
  const totalMs = Math.max(0, Math.floor(ms));
  const mm = Math.floor(totalMs / 60000);
  const ss = Math.floor((totalMs % 60000) / 1000);
  const mmm = totalMs % 1000;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}.${String(mmm).padStart(3, "0")}`;
}

type Draft = { code: string; scriptId: number | null; scriptName: string; savedCode: string };

export default function SimulatorPage() {
  const { user, logout } = useAuth();
  const sim = useSimulator();
  const [code, setCode] = useState(STARTER);
  const [speed, setSpeed] = useState<Speed>("realtime");
  const [fontSize, setFontSize] = useState(14);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bottomTab, setBottomTab] = useState<BottomTab>("console");
  const [devFilter, setDevFilter] = useState<"all" | SimEvent["dev"]>("all");
  const [autoscroll, setAutoscroll] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);

  // Phase 4 persistence state. scriptId is null until saved to the server;
  // savedCode is the last saved/opened buffer, so `dirty` = unsaved edits.
  const [scriptId, setScriptId] = useState<number | null>(null);
  const [scriptName, setScriptName] = useState("untitled.py");
  const [savedCode, setSavedCode] = useState(STARTER);
  const [openOpen, setOpenOpen] = useState(false);
  const [apiRefOpen, setApiRefOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const dirty = code !== savedCode;

  // Per-user draft key so a shared classroom machine never shows one student's
  // draft to another.
  const draftKey = `mekaboy:draft:v1:${user?.id ?? "anon"}`;

  const editorRef = useRef<MonacoEditor | null>(null);
  const monacoRef = useRef<MonacoNs | null>(null);
  const logScrollRef = useRef<HTMLDivElement | null>(null);

  // --- draft autosave / restore (spec §6.2: localStorage every 10 s) --------
  const draftRef = useRef<Draft>({ code, scriptId, scriptName, savedCode });
  draftRef.current = { code, scriptId, scriptName, savedCode };

  // Restore a crash-recovery draft once on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const d = JSON.parse(raw) as Draft;
      if (typeof d.code !== "string") return;
      setCode(d.code);
      setSavedCode(typeof d.savedCode === "string" ? d.savedCode : d.code);
      setScriptId(typeof d.scriptId === "number" ? d.scriptId : null);
      setScriptName(d.scriptName || "untitled.py");
      if (d.code !== (d.savedCode ?? d.code)) {
        setNotice("Restored your unsaved draft from this browser.");
      }
    } catch {
      /* ignore a corrupt draft */
    }
  }, []);

  // Persist the draft every 10 s (reads the latest values via a ref).
  useEffect(() => {
    const id = setInterval(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify(draftRef.current));
      } catch {
        /* storage full / disabled — non-fatal */
      }
    }, 10_000);
    return () => clearInterval(id);
  }, [draftKey]);

  // Warn before leaving with unsaved changes (spec §6.2 unsaved-changes guard).
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // --- file workflow handlers ----------------------------------------------
  function discardOk(): boolean {
    return !dirty || window.confirm("You have unsaved changes. Discard them?");
  }

  function onNew() {
    if (!discardOk()) return;
    setCode("");
    setSavedCode("");
    setScriptId(null);
    setScriptName("untitled.py");
    sim.clearLog();
  }

  function onOpenResult(result: OpenResult) {
    setOpenOpen(false);
    setCode(result.code);
    setSavedCode(result.code);
    setScriptId(result.id);
    setScriptName(ensurePyName(result.name));
    sim.clearLog();
  }

  function saveErr(e: unknown): string {
    return e instanceof ApiError
      ? e.message
      : "Could not save — check your connection and try again.";
  }

  async function saveAs() {
    const input = window.prompt(
      "Save script as (name must end in .py):",
      scriptName || "untitled.py",
    );
    if (input === null) return;
    const name = ensurePyName(input);
    const snapshot = code;
    try {
      const saved = await api.createScript(name, snapshot);
      setScriptId(saved.id);
      setScriptName(saved.name);
      setSavedCode(snapshot);
      setNotice(`Saved “${saved.name}”.`);
    } catch (e) {
      setNotice(saveErr(e));
    }
  }

  async function save() {
    if (scriptId === null) {
      await saveAs();
      return;
    }
    const snapshot = code;
    try {
      const saved = await api.updateScript(scriptId, { name: scriptName, code: snapshot });
      setSavedCode(snapshot);
      setScriptName(saved.name);
      setNotice(`Saved “${saved.name}”.`);
    } catch (e) {
      setNotice(saveErr(e));
    }
  }

  function onDownload() {
    downloadScript(scriptName, code);
  }

  // Ctrl/Cmd+S saves without leaving the editor.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void saveRef.current();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // Autoscroll the bottom panel to the newest line when enabled.
  useEffect(() => {
    if (autoscroll && logScrollRef.current) {
      logScrollRef.current.scrollTop = logScrollRef.current.scrollHeight;
    }
  }, [autoscroll, bottomTab, sim.events, sim.stdout]);

  function revealLine(line: number) {
    const editor = editorRef.current;
    if (!editor) return;
    editor.revealLineInCenter(line);
    editor.setPosition({ lineNumber: line, column: 1 });
    editor.focus();
  }

  // Place / clear a red Monaco marker on the error line (spec §4.4).
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco) return;
    const model = editor.getModel();
    if (!model) return;
    if (sim.error && sim.error.line) {
      monaco.editor.setModelMarkers(model, "sim", [
        {
          startLineNumber: sim.error.line,
          endLineNumber: sim.error.line,
          startColumn: 1,
          endColumn: model.getLineMaxColumn(sim.error.line),
          severity: monaco.MarkerSeverity.Error,
          message: sim.error.message,
        },
      ]);
      revealLine(sim.error.line);
    } else {
      monaco.editor.setModelMarkers(model, "sim", []);
    }
  }, [sim.error]);

  // The Console auto-activates when a run produces an error (spec §6.2).
  useEffect(() => {
    if (sim.error) setBottomTab("console");
  }, [sim.error]);

  const filteredEvents = useMemo(
    () =>
      devFilter === "all"
        ? // Pose updates (~10 Hz) drive the world map, not the log — hide them here.
          sim.events.filter((e) => e.dev !== "pose")
        : sim.events.filter((e) => e.dev === devFilter),
    [sim.events, devFilter],
  );

  const statusLabel =
    sim.status === "loading"
      ? "loading interpreter…"
      : sim.status === "running"
        ? "running"
        : sim.status === "stopped"
          ? "stopped"
          : "ready";

  const running = sim.status === "running";
  const canRun = sim.status === "ready" || sim.status === "stopped";

  return (
    <div className="flex h-full flex-col bg-slate-900 text-slate-100">
      <header className="flex items-center justify-between border-b border-slate-700 bg-slate-800 px-4 py-2">
        <div className="flex items-center gap-2">
          <img src={mekaboyLogoWhite} alt="" className="h-7 w-auto" />
          <span className="font-semibold text-slate-100">Mekaboy Simulator</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-300">
          {user?.role === "admin" && (
            <Link
              to="/admin"
              className="rounded bg-slate-700 px-3 py-1 font-medium text-slate-100 hover:bg-slate-600"
            >
              Admin
            </Link>
          )}
          <span>
            {user?.display_name} ({user?.role})
          </span>
          <button
            type="button"
            onClick={() => setPwOpen(true)}
            className="rounded bg-slate-700 px-3 py-1 font-medium text-slate-100 hover:bg-slate-600"
          >
            Change password
          </button>
          <button
            type="button"
            onClick={logout}
            className="rounded bg-slate-700 px-3 py-1 font-medium hover:bg-slate-600"
          >
            Log out
          </button>
        </div>
      </header>

      <Toolbar
        speed={speed}
        onToggleSpeed={() => setSpeed((s) => (s === "realtime" ? "instant" : "realtime"))}
        fontSize={fontSize}
        onFontSize={(d) => setFontSize((f) => Math.min(28, Math.max(10, f + d)))}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((o) => !o)}
        onRun={() => sim.run(code, speed)}
        onStop={sim.stop}
        canRun={canRun}
        running={running}
        scriptName={scriptName}
        dirty={dirty}
        onNew={onNew}
        onOpen={() => setOpenOpen(true)}
        onSave={save}
        onSaveAs={saveAs}
        onDownload={onDownload}
        onApiRef={() => setApiRefOpen(true)}
      />

      {notice && (
        <div className="flex items-center justify-between bg-amber-100 px-3 py-1.5 text-sm text-amber-900">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}

      {openOpen && <OpenDialog onClose={() => setOpenOpen(false)} onOpen={onOpenResult} />}
      {apiRefOpen && <ApiReference onClose={() => setApiRefOpen(false)} />}
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-[3] border-b border-slate-700">
            <CodeEditor
              value={code}
              onChange={setCode}
              fontSize={fontSize}
              onRun={() => sim.run(code, speed)}
              onReady={(editor, monaco) => {
                editorRef.current = editor;
                monacoRef.current = monaco;
              }}
            />
          </div>

          <div className="flex min-h-0 flex-[2] flex-col bg-slate-950">
            <div className="flex items-center border-b border-slate-700 text-sm">
              <button
                type="button"
                className={`px-4 py-2 ${bottomTab === "console" ? "border-b-2 border-sky-400 text-sky-300" : "text-slate-400"}`}
                onClick={() => setBottomTab("console")}
              >
                Console
              </button>
              <button
                type="button"
                className={`px-4 py-2 ${bottomTab === "actionlog" ? "border-b-2 border-sky-400 text-sky-300" : "text-slate-400"}`}
                onClick={() => setBottomTab("actionlog")}
              >
                Action Log{sim.events.length ? ` (${sim.events.length})` : ""}
              </button>
              <div className="ml-auto flex items-center gap-2 px-3">
                {bottomTab === "actionlog" && (
                  <select
                    className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300"
                    value={devFilter}
                    onChange={(e) => setDevFilter(e.target.value as typeof devFilter)}
                  >
                    <option value="all">all devices</option>
                    {Object.keys(DEV_ICON).map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  className={`rounded px-2 py-1 text-xs ${
                    autoscroll ? "bg-sky-700 text-white" : "bg-slate-800 text-slate-300"
                  } hover:opacity-90`}
                  onClick={() => setAutoscroll((a) => !a)}
                  title="Scroll to newest line automatically"
                >
                  Autoscroll: {autoscroll ? "On" : "Off"}
                </button>
                <button
                  type="button"
                  className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700"
                  onClick={sim.clearLog}
                >
                  Clear
                </button>
              </div>
            </div>

            <div ref={logScrollRef} className="flex-1 overflow-auto p-3 font-mono text-xs">
              {bottomTab === "console" ? (
                <ConsoleView stdout={sim.stdout} error={sim.error} onJump={revealLine} />
              ) : (
                <ActionLogView events={filteredEvents} />
              )}
            </div>
          </div>
        </div>

        {sidebarOpen && (
          <aside className="w-80 shrink-0 overflow-auto border-l border-slate-700 bg-slate-800 p-3">
            <SensorPanel sim={sim} />
          </aside>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-300">
        <span className="flex items-center gap-2">
          <img src={mctLogoSmall} alt="" className="h-4 w-auto" />
          Interpreter: {statusLabel} · mode{" "}
          {speed === "realtime"
            ? `Real-time (${sim.liveAvailable ? "live" : "replay"})`
            : "Instant"}
        </span>
        <span>elapsed sim time {fmtTime(sim.elapsedSimMs)}</span>
      </div>
    </div>
  );
}

function ConsoleView({
  stdout,
  error,
  onJump,
}: {
  stdout: { t: number; text: string }[];
  error: import("../worker/protocol").SimError | null;
  onJump: (line: number) => void;
}) {
  if (!stdout.length && !error) {
    return <span className="text-slate-500">Console output will appear here when you Run.</span>;
  }
  return (
    <div className="whitespace-pre-wrap">
      {stdout.map((c, i) => (
        <div key={i} className="text-slate-200">
          {c.text}
        </div>
      ))}
      {error && (
        <div className="mt-2 rounded border border-red-700 bg-red-950/40 p-2 text-red-300">
          <div className="font-semibold">
            {error.kind === "syntax"
              ? "Syntax error"
              : error.kind === "budget"
                ? "Simulation stopped"
                : "Error"}
            {error.line ? (
              <button
                type="button"
                className="ml-2 underline hover:text-red-200"
                onClick={() => onJump(error.line!)}
              >
                jump to line {error.line}
              </button>
            ) : null}
          </div>
          <div>{error.message}</div>
          {error.hint && <div className="mt-1 text-amber-300">💡 {error.hint}</div>}
        </div>
      )}
    </div>
  );
}

function ActionLogView({ events }: { events: SimEvent[] }) {
  if (!events.length) {
    return <span className="text-slate-500">Robot events will appear here when you Run.</span>;
  }
  return (
    <div>
      {events.map((e, i) => {
        const isEnd = e.dev === "system" && e.op === "end";
        const isBlocked = e.dev === "system" && e.op === "blocked";
        const rowColor = isEnd ? "text-emerald-300" : isBlocked ? "text-amber-300" : "";
        const icon = isEnd ? "🏁" : isBlocked ? "⛔" : DEV_ICON[e.dev];
        return (
          <div key={i} className={`flex gap-2 border-b border-slate-800/60 py-0.5 ${rowColor}`}>
            <span className="w-24 shrink-0 text-slate-500">{fmtTime(e.t)}</span>
            <span className="w-5 shrink-0">{icon}</span>
            <span className={isEnd || isBlocked ? "font-semibold" : "text-slate-200"}>
              {e.desc}
            </span>
          </div>
        );
      })}
    </div>
  );
}
